import type { BoardSummary, SpaceSummary } from '@doriavboard/shared';
import { Check, FolderOpen, LayoutGrid } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import { boardsApi, spacesApi } from '../api/endpoints';
import { cn } from '../lib/cn';
import { spacePeopleLabel } from '../lib/users';
import { useSession } from '../stores/session';
import { toast } from '../stores/toasts';
import { Modal } from './ui/Modal';
import { Spinner } from './ui/Spinner';

interface MoveToSpaceDialogProps {
  board: BoardSummary;
  open: boolean;
  onOpenChange(open: boolean): void;
  onMoved(board: BoardSummary): void;
}

/** Puts a board in one of the spaces the user can add boards to, or takes it out. */
export function MoveToSpaceDialog({ board, open, onOpenChange, onMoved }: MoveToSpaceDialogProps) {
  const me = useSession((s) => s.user);
  const [spaces, setSpaces] = useState<SpaceSummary[] | null>(null);
  const [moving, setMoving] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSpaces(null);
    spacesApi
      .list()
      .then(({ spaces: list }) => setSpaces(list.filter((s) => s.role !== 'viewer')))
      .catch((err) => {
        toast(errorMessage(err), 'error');
        setSpaces([]);
      });
  }, [open]);

  async function move(spaceId: string | null) {
    if (spaceId === (board.space?.id ?? null)) {
      onOpenChange(false);
      return;
    }
    setMoving(spaceId ?? '');
    try {
      const { board: updated } = await boardsApi.update(board.id, { spaceId });
      onMoved(updated);
      toast(updated.space ? `Moved to “${updated.space.name}”` : 'Moved to your boards', 'success');
      onOpenChange(false);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setMoving(null);
    }
  }

  function option(id: string | null, icon: ReactNode, label: string, detail: string) {
    const current = id === (board.space?.id ?? null);
    return (
      <li key={id ?? 'none'}>
        <button
          type="button"
          disabled={moving !== null}
          onClick={() => move(id)}
          className={cn(
            'focus-ring flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-100 disabled:opacity-60 dark:hover:bg-slate-800',
            current && 'bg-brand-50 dark:bg-brand-500/10',
          )}
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {icon}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{label}</span>
            <span className="block truncate text-xs text-slate-500">{detail}</span>
          </span>
          {moving === (id ?? '') ? (
            <Spinner className="size-4 text-slate-400" />
          ) : (
            current && <Check className="size-4 text-brand-600 dark:text-brand-400" />
          )}
        </button>
      </li>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Move “${board.title}”`}
      description="Everyone in a space can open the boards in it."
    >
      {spaces === null ? (
        <div className="flex justify-center py-6">
          <Spinner className="size-6 text-slate-400" />
        </div>
      ) : (
        <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
          {option(
            null,
            <LayoutGrid className="size-4" />,
            'No space',
            'Only the people it’s shared with',
          )}
          {spaces.map((s) =>
            option(s.id, <FolderOpen className="size-4" />, s.name, spacePeopleLabel(s, me?.id)),
          )}
          {spaces.length === 0 && (
            <li className="px-2 py-3 text-sm text-slate-500">
              You have no spaces yet. Create one from the dashboard to group boards for the same
              people.
            </li>
          )}
        </ul>
      )}
    </Modal>
  );
}
